// The time-off heatmap toolbar: year navigation (prev/next/this-year), type
// chip filters (Holiday/Vacation/Time off — accent-colored), scope chip
// filters (Global/Per-doctor), and a search box (debounced by the parent).
//
// Mirrors `slots-heatmap/toolbar.jsx` — same row layout, same chip pill style,
// same h-7 NavButton sizing. The only structural change is a second chip row
// for scope (slot's tenant-chip equivalent doesn't apply here).

import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { entryAccent } from "./time-off-utils";

const TYPE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "holiday", label: "Holiday" },
  { value: "vacation", label: "Vacation" },
  { value: "block", label: "Time off" },
];

const SCOPE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "global", label: "Global" },
  { value: "doctor", label: "Per-doctor" },
];

// One pill in a filter row. Optional `colorHex` adds a leading dot in the
// type's accent color (mirrors how slots' ChipFilter shows a tenant-hue dot).
function ChipFilter({ active, colorHex, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-[11px] font-semibold transition-colors",
        active
          ? "bg-accent text-foreground"
          : "bg-muted text-muted-foreground hover:text-foreground",
      )}
    >
      {colorHex ? (
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ background: colorHex }}
        />
      ) : null}
      {children}
    </button>
  );
}

// Compact h-7 button for year-navigation controls (prev / next / this-year).
function NavButton({ onClick, ariaLabel, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={title}
      className="inline-flex h-7 items-center justify-center rounded-md border border-border bg-card px-2 text-[11px] font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      {children}
    </button>
  );
}

export function Toolbar({
  year,
  isCurrentYear,
  onPrevYear,
  onNextYear,
  onThisYear,
  typeFilter,
  onTypeFilter,
  scopeFilter,
  onScopeFilter,
  search,
  onSearchChange,
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border bg-muted/30 px-5 py-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Year</span>
          <div className="flex items-center gap-1">
            <NavButton
              onClick={onPrevYear}
              ariaLabel="Previous year"
              title="Previous year"
            >
              <ChevronLeft className="size-3.5" />
            </NavButton>
            <span className="rounded-md border border-border bg-card px-2 py-1 font-mono tabular-nums text-foreground/85">
              {year}
            </span>
            <NavButton
              onClick={onNextYear}
              ariaLabel="Next year"
              title="Next year"
            >
              <ChevronRight className="size-3.5" />
            </NavButton>
            {!isCurrentYear ? (
              <NavButton
                onClick={onThisYear}
                ariaLabel="Jump to the current year"
                title="Jump to the current year"
              >
                This year
              </NavButton>
            ) : null}
          </div>
        </div>

        <span className="text-muted-foreground/50">·</span>

        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Type</span>
          <div className="flex flex-wrap items-center gap-1">
            {TYPE_OPTIONS.map((opt) => (
              <ChipFilter
                key={opt.value}
                active={typeFilter === opt.value}
                colorHex={
                  opt.value === "all" ? null : entryAccent({ type: opt.value }).bandHex
                }
                onClick={() => onTypeFilter(opt.value)}
              >
                {opt.label}
              </ChipFilter>
            ))}
          </div>
        </div>

        <span className="text-muted-foreground/50">·</span>

        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Scope</span>
          <div className="flex flex-wrap items-center gap-1">
            {SCOPE_OPTIONS.map((opt) => (
              <ChipFilter
                key={opt.value}
                active={scopeFilter === opt.value}
                onClick={() => onScopeFilter(opt.value)}
              >
                {opt.label}
              </ChipFilter>
            ))}
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground/60" />
          <Input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Find doctor or title…"
            className="h-7 w-52 pl-7 text-xs"
          />
        </div>
      </div>
    </div>
  );
}
